import { useEffect, useState } from 'react';
import { api, ago, humanise, type PersonDetail, type SupportContext } from '../api';
import { Panel, ShortId, State, kycTone, tripTone } from '../ui';
import { TripMap } from '../TripMap';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const daysOf = (mask: number) =>
  mask === 127 ? 'Every day' : mask === 31 ? 'Mon–Fri' : mask === 63 ? 'Mon–Sat'
    : DAYS.filter((_, i) => mask & (1 << i)).join(', ') || '—';

const live = (s: string) => ['proposed', 'pending', 'active'].includes(s);

/**
 * Everything about one rider or driver: who they are, their routes, rides,
 * trips and support history, and — for drivers — vehicle, verification and
 * (admins only) every uploaded document. The support dashboard uses the same
 * page without documents, plus a button to message the person.
 */
export function Person({
  id, mode, onClose, onMessage,
}: {
  id: string;
  mode: 'admin' | 'support';
  onClose: () => void;
  /** Opens a support conversation with this person (support desk). */
  onMessage?: (userId: string, tripId?: string) => void;
}) {
  const [p, setP] = useState<PersonDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [rideOpen, setRideOpen] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState<string | null>(null);

  useEffect(() => {
    setP(null);
    setErr(null);
    api.person(id, mode).then(setP).catch((e) => setErr(e instanceof Error ? e.message : 'Could not load'));
  }, [id, mode]);

  if (err) return <Panel title="Person"><div className="empty">{err}</div></Panel>;
  if (!p) return <Panel title="Person"><div className="empty">Loading…</div></Panel>;
  const u = p.user;

  return (
    <div className="person">
      <Panel
        title={u.fullName ?? u.phone ?? 'Unnamed'}
        right={
          <span className="row">
            {onMessage ? <button className="btn sm primary" onClick={() => onMessage(u.id)}>Message</button> : null}
            <button className="btn sm" onClick={onClose}>Close</button>
          </span>
        }
      >
        <div className="pad who">
          {u.avatarUrl ? <img src={u.avatarUrl} alt="" className="avatar" /> : <div className="avatar none">{(u.fullName ?? '?').slice(0, 1)}</div>}
          <dl className="facts">
            <dt>Role</dt><dd>{humanise(u.role)}{u.status !== 'active' ? <> · <State tone="bad">{u.status}</State></> : null}</dd>
            <dt>Phone</dt><dd>{u.phone ? <a href={`tel:${u.phone}`}>{u.phone}</a> : '—'}</dd>
            <dt>Email</dt><dd>{u.email ?? '—'}</dd>
            <dt>Joined</dt><dd>{new Date(u.createdAt).toLocaleDateString()} ({ago(u.createdAt)})</dd>
            <dt>Rating</dt><dd>{u.rating == null ? '—' : u.rating.toFixed(1)}</dd>
            {u.role !== 'driver' ? <><dt>Free rides left</dt><dd>{u.freeRidesLeft}</dd></> : null}
            <dt>Shown to co-riders as</dt><dd>{u.nickname ?? 'Rider A/B (no nickname)'}</dd>
            <dt>Gender · preference</dt><dd>{humanise(u.gender ?? 'not given')} · {humanise(u.ridePreference)}</dd>
          </dl>
        </div>
      </Panel>

      {p.driver ? (
        <Panel title="Driver" right={<State tone={kycTone(p.driver.kycStatus)}>{humanise(p.driver.kycStatus)}</State>}>
          <div className="pad">
            <p className="hint">
              Onboarding step {p.driver.onboardingStep} of 4{p.driver.residenceAddress ? ` · lives at ${p.driver.residenceAddress}` : ''}
            </p>
            {p.driver.vehicles.length ? (
              <table className="checks">
                <thead><tr><th>Vehicle</th><th>Plate</th><th>Seats</th><th /></tr></thead>
                <tbody>
                  {p.driver.vehicles.map((v) => (
                    <tr key={v.id}>
                      <td>{[v.color, v.make, v.model, v.model_year].filter(Boolean).join(' ')}</td>
                      <td>
                        <span className="plate">{v.registration_no}</span>
                        {v.plate_colour === 'white' ? <span className="flag" style={{ marginLeft: 6 }}>white plate</span> : null}
                      </td>
                      <td>{v.seats_passenger}</td>
                      <td>{v.is_active ? <State tone="ok">active</State> : <span className="muted">old</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="hint">No vehicle added yet.</p>}

            <h3 className="sub">Verification</h3>
            {p.driver.verifications.length ? (
              <table className="checks">
                <thead><tr><th>Requirement</th><th>Method</th><th>Status</th><th>Files</th><th>Submitted</th></tr></thead>
                <tbody>
                  {p.driver.verifications.map((v) => (
                    <tr key={v.id}>
                      <td>{humanise(v.requirement)}</td>
                      <td>{humanise(v.method)}</td>
                      <td><State tone={kycTone(v.status)}>{humanise(v.status)}</State></td>
                      <td>{v.files}</td>
                      <td>{ago(v.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="hint">Nothing submitted yet.</p>}

            {p.driver.documents ? (
              <>
                <h3 className="sub">Uploaded documents ({p.driver.documents.length})</h3>
                {p.driver.documents.length ? (
                  <div className="docs">
                    {p.driver.documents.map((d) => (
                      <a key={d.id} href={d.url ?? undefined} target="_blank" rel="noreferrer" className="doc">
                        {d.url && (d.mimeType ?? 'image').startsWith('image') ? <img src={d.url} alt={d.kind} /> : <span className="docfile">PDF</span>}
                        <span>{humanise(d.kind)}</span>
                        <small className="detail">{humanise(d.status)} · {ago(d.createdAt)}</small>
                      </a>
                    ))}
                  </div>
                ) : <p className="hint">No uploads yet.</p>}
              </>
            ) : null}
          </div>
        </Panel>
      ) : null}

      <Panel title={`Routes (${p.routes.length})`}>
        {p.routes.length ? (
          <table className="checks">
            <thead><tr><th>Route</th><th>When</th><th>Days</th><th>Distance</th><th /></tr></thead>
            <tbody>
              {p.routes.map((r) => (
                <tr key={r.id} className={r.isActive ? undefined : 'faded'}>
                  <td>
                    <strong>{humanise(r.direction)}</strong> · {humanise(r.ownerRole)}
                    <small className="detail">{r.pickupAddress} → {r.dropAddress}</small>
                  </td>
                  <td>{r.window ?? (r.departAt ? `leaves ${r.departAt}` : '—')}</td>
                  <td>{daysOf(r.daysMask)}</td>
                  <td>{(r.distanceM / 1000).toFixed(1)} km</td>
                  <td>{r.isActive ? <State tone="ok">active</State> : <span className="muted">replaced</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="empty">No routes set up.</div>}
      </Panel>

      <Panel title={`Rides (${p.rides.filter((r) => live(r.status)).length} current)`}>
        {p.rides.length ? (
          <table className="checks">
            <thead><tr><th>Status</th><th>Driver</th><th>Rider</th><th>Driver's route</th><th>Since</th></tr></thead>
            <tbody>
              {p.rides.map((m) => (
                <tr key={m.id} className={live(m.status) ? undefined : 'faded'}>
                  <td><State tone={live(m.status) ? 'ok' : 'idle'}>{humanise(m.status)}</State>{m.chosen ? <small className="detail">picked by rider</small> : null}</td>
                  <td>{m.driver.name ?? '—'}<small className="detail">{m.driver.phone}</small></td>
                  <td>{m.rider.name ?? '—'}<small className="detail">{m.rider.phone}</small></td>
                  <td><small>{m.driverRoute}</small></td>
                  <td>{ago(m.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="empty">No rides yet.</div>}
      </Panel>

      <Panel title={`Trips (${p.trips.length})`}>
        {p.trips.length ? (
          <table className="checks">
            <thead><tr><th>Date</th><th>As</th><th>Status</th><th>Driver</th><th>Riders</th><th /></tr></thead>
            <tbody>
              {p.trips.map((t) => (
                <tr key={t.id}>
                  <td>{t.serviceDate} · {humanise(t.direction)}</td>
                  <td>{humanise(t.asRole)}{t.legStatus ? <small className="detail">{humanise(t.legStatus)}</small> : null}</td>
                  <td><State tone={tripTone(t.status)}>{humanise(t.status)}</State></td>
                  <td>{t.driverName ?? '—'}</td>
                  <td>{t.riders}</td>
                  <td>
                    <span className="row">
                      <button className="btn sm" onClick={() => setRideOpen(rideOpen === t.id ? null : t.id)}>Details</button>
                      <button className="btn sm" onClick={() => setMapOpen(t.id)}>Route</button>
                      {onMessage ? <button className="btn sm" onClick={() => onMessage(u.id, t.id)}>Ask about it</button> : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="empty">No trips yet.</div>}
        {rideOpen ? <Ride tripId={rideOpen} /> : null}
      </Panel>

      <Panel title={`Support conversations (${p.tickets.length})`}>
        {p.tickets.length ? (
          <table className="checks">
            <tbody>
              {p.tickets.map((t) => (
                <tr key={t.id}>
                  <td><State tone={t.status === 'resolved' ? 'ok' : 'warn'}>{humanise(t.status)}</State>{t.callback ? <small className="detail">asked for a call</small> : null}</td>
                  <td>{t.last_message ?? '—'}</td>
                  <td>{ago(t.created_at)}</td>
                  <td><ShortId id={t.id} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="empty">Never contacted support.</div>}
      </Panel>

      {mapOpen ? <TripMap tripId={mapOpen} onClose={() => setMapOpen(null)} /> : null}
    </div>
  );
}

/** One ride as agents see it: driver, vehicle, every rider with numbers. */
export function Ride({ tripId }: { tripId: string }) {
  const [r, setR] = useState<NonNullable<SupportContext['trip']> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setR(null);
    api.ride(tripId).then(setR).catch((e) => setErr(e instanceof Error ? e.message : 'Could not load'));
  }, [tripId]);
  if (err) return <p className="hint bad pad">{err}</p>;
  if (!r) return <p className="hint pad">Loading the ride…</p>;
  return (
    <div className="pad ridebox">
      <dl className="facts">
        <dt>Ride</dt><dd>{r.serviceDate} · {humanise(r.direction)} · {humanise(r.status)}</dd>
        <dt>Driver</dt><dd>{r.driver.name ?? '—'}{r.driver.phone ? <> · <a href={`tel:${r.driver.phone}`}>{r.driver.phone}</a></> : null}</dd>
        <dt>Vehicle</dt><dd>{[r.vehicle.colour, r.vehicle.make, r.vehicle.model].filter(Boolean).join(' ')} · <span className="plate">{r.vehicle.registrationNo}</span></dd>
        <dt>Live position</dt><dd>{r.position ? `${r.position.lat.toFixed(5)}, ${r.position.lng.toFixed(5)}` : 'Not moving'}</dd>
        <dt>Riders</dt>
        <dd>
          <ol className="riders">
            {r.riders.map((x) => (
              <li key={x.seq}>
                {x.name ?? 'Rider'} — {humanise(x.status)}
                {x.phone ? <> · <a href={`tel:${x.phone}`}>{x.phone}</a></> : null}
                {x.pickupAddress ? <small> · from {x.pickupAddress}</small> : null}
              </li>
            ))}
          </ol>
        </dd>
      </dl>
    </div>
  );
}
